"use client";

import React from 'react'
import { motion } from 'framer-motion'
import { fadeInUp } from '@/lib/motion-variants'
import { cn } from '@/lib/utils'
import { motionConfig as motionSettings } from '@/config/motion'

interface TypographyProps extends React.HTMLAttributes<HTMLElement> {
  animateInView?: boolean
}

function createTypography(tag: keyof typeof motion, baseClass: string) {
  const Component = ({
    children,
    className,
    animateInView = motionSettings.enableAnimations,
    ...props
  }: TypographyProps) => {
    const classes = cn(baseClass, className)

    if (!animateInView) {
      return React.createElement(tag, { className: classes, ...props }, children)
    }

    const MotionTag = (motion as unknown as Record<string, React.ElementType>)[tag]
    return (
      <MotionTag
        className={classes}
        initial="initial"
        whileInView="animate"
        viewport={{ once: motionSettings.inViewOnce, amount: motionSettings.inViewAmount }}
        exit="exit"
        variants={fadeInUp}
        {...props}
      >
        {children}
      </MotionTag>
    )
  }

  Component.displayName = `Typography${tag.toUpperCase()}`
  return Component
}

export const TypographyH1 = createTypography(
  'h1',
  'font-heading scroll-m-20 text-4xl font-extrabold tracking-tight text-balance'
)

export const TypographyH2 = createTypography(
  'h2',
  'font-heading scroll-m-20 text-3xl font-semibold tracking-tight text-balance '
)

export const TypographyH3 = createTypography(
  'h3',
  'font-heading scroll-m-20 text-2xl font-semibold tracking-tight'
)

export const TypographyH4 = createTypography(
  'h4',
  'font-heading scroll-m-20 text-xl font-semibold tracking-tight'
)

export const TypographyP = createTypography('p', 'font-body')

export const TypographyLead = createTypography(
  'p',
  'font-body text-lg text-muted-foreground'
)

export const TypographyList = createTypography(
  'ul',
  'list-disc pl-6 space-y-2'
)

export const TypographyListItem = createTypography('li', '')
